import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import FilterBar from '../components/FilterBar';
import Panel from '../components/Panel';

import WeekNote from '../components/WeekNote';

function InsightCard({ label, title, body }) {
  return (
    <div style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '24px', padding: '24px', boxShadow: 'var(--shadow-md)',
    }}>
      <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#8b7355', marginBottom: '12px' }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '32px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-dark)', lineHeight: 1.3, marginBottom: '12px' }}>
        {title}
      </div>
      <p style={{ fontSize: '14px', lineHeight: 1.75, color: 'var(--text-light)', margin: 0 }}>{body}</p>
    </div>
  );
}

function KpiCard({ label, value, note, accent = 'blue' }) {
  const colors = { blue: '#1f5eff', green: '#1f7a3f', amber: '#c98b12', teal: '#0f766e', red: '#c2410c' };
  return (
    <div className="metric-card">
      <span className="metric-label">{label}</span>
      <div className="metric-value" style={{ color: colors[accent] || colors.blue }}>{value}</div>
      {note && <div style={{ fontSize: '12px', color: 'var(--text-lighter)', marginTop: '6px' }}>{note}</div>}
    </div>
  );
}

function SignalTable({ rows, tone }) {
  if (!rows.length) return (
    <div style={{ border: '1.5px dashed var(--card-border)', borderRadius: '16px', padding: '32px 16px', textAlign: 'center', fontSize: '13px', color: 'var(--text-lighter)' }}>
      No signals detected in current scope.
    </div>
  );
  const bg = tone === 'pain' ? 'rgba(194,65,12,0.08)' : 'rgba(31,122,63,0.08)';
  const fg = tone === 'pain' ? '#c2410c' : '#1f7a3f';
  const tag = tone === 'pain' ? 'Pain' : 'Positive';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {rows.map(row => (
        <div key={row.signal_name} style={{
          background: 'var(--bg-dark)', border: '1px solid var(--card-border)',
          borderRadius: '16px', padding: '14px 16px',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
        }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-dark)', fontSize: '14px' }}>{row.signal_name}</div>
            <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--text-light)' }}>
              {row.mention_count} mentions
              {row.mention_share != null ? ` · ${Math.round(Number(row.mention_share) * 100)}%` : ''}
            </div>
          </div>
          <span style={{ background: bg, color: fg, borderRadius: '999px', padding: '3px 12px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {tag}
          </span>
        </div>
      ))}
    </div>
  );
}

function pct(v) { return v == null ? '0%' : `${Math.round(Number(v) * 100)}%`; }
function num(v, d = 2) { return v == null ? '0.00' : Number(v).toFixed(d); }

function FieldSignal({ filters, onDrilldown, loading, setLoading }) {
  const [summary, setSummary] = useState({});
  const [trends, setTrends] = useState([]);
  const [painSignals, setPainSignals] = useState([]);
  const [positiveSignals, setPositiveSignals] = useState([]);
  const [distribution, setDistribution] = useState([]);
  const [intentTrends, setIntentTrends] = useState([]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== null) params.append(k, v); });

    axios.get(`/api/customer-insight?${params}`)
      .then(res => {
        if (!active) return;
        setSummary(res.data.summary || {});
        setTrends(res.data.trends || []);
        setPainSignals(res.data.painSignals || []);
        setPositiveSignals(res.data.positiveSignals || []);
        setDistribution(res.data.distribution || []);
        setIntentTrends(res.data.intentTrends || []);
      })
      .catch(err => { if (active) console.error('Error loading Customer Insight:', err); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, setLoading]);

  const topPain = painSignals[0];
  const topPositive = positiveSignals[0];
  const topDimension = distribution[0];
  const strongestIntentWeek = [...intentTrends].sort((a, b) => Number(b.intent_share || 0) - Number(a.intent_share || 0))[0];

  return (
    <div>
      {/* Header */}
      <div className="module-header">
        <div style={{ fontSize: '11px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.22em', color: 'var(--text-lighter)', marginBottom: '6px' }}>
          Field Intelligence
        </div>
        <h2>Customer Insight</h2>
        <p>A live read on customer pain, positive response, buyer intent, and unresolved friction across the selected scope.</p>
      </div>

      {/* Filter Bar */}
      <FilterBar />

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <KpiCard label="Conversations" value={summary.total_conversations ?? 0} note="Included in current scope" accent="blue" />
        <KpiCard label="Pain Signal %" value={pct(summary.avg_pain_ratio)} note="Avg pain intensity" accent="red" />
        <KpiCard label="Positive Signal %" value={pct(summary.avg_positive_ratio)} note="Trust and value presence" accent="green" />
        <KpiCard label="Avg Sentiment" value={num(summary.avg_sentiment)} note="Conversation-level sentiment" accent="teal" />
        <KpiCard label="Unresolved Issues" value={summary.unresolved_issues ?? 0} note="Open friction indicators" accent="amber" />
      </div>

      {/* Insight Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '32px' }}>
        <InsightCard
          label="Primary Pain"
          title={topPain ? `${topPain.signal_name} is the strongest customer friction point.` : 'No dominant pain signal yet.'}
          body={topPain
            ? `${topPain.signal_name} is the most frequent pain pattern in the selected scope — the clearest live issue to address.`
            : 'Widen the current scope to surface a stronger customer pain pattern.'}
        />
        <InsightCard
          label="Positive Lead"
          title={topPositive ? `${topPositive.signal_name} is the strongest trust indicator.` : 'No dominant positive signal yet.'}
          body={topPositive
            ? `${topPositive.signal_name} represents the strongest positive response pattern in the selected scope, indicating where value is landing.`
            : 'Widen the scope or increase volume to surface a clear positive signal.'}
        />
        <InsightCard
          label="Intent Read"
          title={strongestIntentWeek ? `Intent peaked around ${strongestIntentWeek.week_start}.` : 'No clear intent peak yet.'}
          body={strongestIntentWeek
            ? `The highest buyer intent signals were concentrated around ${strongestIntentWeek.week_start}. The dominant signal dimension in the scope is ${topDimension?.dashboard_dimension || 'mixed'}.`
            : 'Expand the scope window to locate a meaningful intent concentration.'}
        />
      </div>

      {/* Pain / Positive Trend Chart */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Pain & Positive Trend" subtitle="Weekly pain and positive signal ratios">
          {trends.length ? (
            <>
              <Plot
                data={[
                  {
                    x: trends.map(t => t.week_start),
                    y: trends.map(t => t.pain_ratio || 0),
                    name: 'Pain Ratio',
                    type: 'scatter', mode: 'lines+markers',
                    line: { color: '#c2410c', width: 2.5 },
                  },
                  {
                    x: trends.map(t => t.week_start),
                    y: trends.map(t => t.positive_ratio || 0),
                    name: 'Positive Ratio',
                    type: 'scatter', mode: 'lines+markers',
                    line: { color: '#1f7a3f', width: 2.5 },
                  },
                ]}
                layout={{
                  xaxis: { title: 'Week starting' },
                  yaxis: { title: 'Ratio', tickformat: '.0%' },
                  plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                  margin: { t: 20, r: 20, b: 50, l: 60 }, height: 280,
                  hovermode: 'x unified', legend: { orientation: 'h', y: -0.25 },
                }}
                config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
                style={{ width: '100%' }}
              />
              <WeekNote />
            </>
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>

        <Panel loading={loading} title="Sentiment Trend" subtitle="Weekly conversation-level sentiment">
          {trends.length ? (
            <>
              <Plot
                data={[{
                  x: trends.map(t => t.week_start),
                  y: trends.map(t => t.sentiment || 0),
                  name: 'Avg Sentiment',
                  type: 'scatter', mode: 'lines+markers',
                  line: { color: '#1f5eff', width: 2.5 },
                  fill: 'tozeroy', fillcolor: 'rgba(31,94,255,0.08)',
                }]}
                layout={{
                  xaxis: { title: 'Week starting' },
                  yaxis: { title: 'Sentiment' },
                  plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                  margin: { t: 20, r: 20, b: 50, l: 60 }, height: 280,
                }}
                config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
                style={{ width: '100%' }}
              />
              <WeekNote />
            </>
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>
      </div>

      {/* Signal Bars — distribution */}
      <div style={{ marginBottom: '32px' }}>
        <Panel loading={loading} title="Signal Distribution" subtitle="Entity types by mention volume (counted once per conversation)">
          {distribution.length ? (
            <Plot
              data={[{
                x: distribution.map(d => d.mention_count),
                y: distribution.map(d => d.dashboard_dimension),
                type: 'bar', orientation: 'h',
                marker: { color: '#1f5eff' },
              }]}
              layout={{
                xaxis: { title: 'Mentions' },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 20, b: 40, l: 160 }, height: 280,
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
          ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
        </Panel>
      </div>

      {/* Signal Tables */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
        <Panel loading={loading} title="Pain Signals" subtitle="Top customer friction entities (counted once per conversation)">
          <SignalTable rows={painSignals.slice(0, 8)} tone="pain" />
        </Panel>
        <Panel loading={loading} title="Positive Signals" subtitle="Top trust and value entities (counted once per conversation)">
          <SignalTable rows={positiveSignals.slice(0, 8)} tone="positive" />
        </Panel>
      </div>

      {/* Intent / Price / Sentiment Trend */}
      <Panel loading={loading} title="Intent & Pain Trend" subtitle="Weekly intent share vs pain share vs sentiment">
        {intentTrends.length ? (
          <>
            <Plot
              data={[
                {
                  x: intentTrends.map(t => t.week_start),
                  y: intentTrends.map(t => t.intent_share || 0),
                  name: 'Intent Share', type: 'scatter', mode: 'lines+markers',
                  line: { color: '#1f5eff', width: 2 },
                },
                {
                  x: intentTrends.map(t => t.week_start),
                  y: intentTrends.map(t => t.pain_share || 0),
                  name: 'Pain Share', type: 'scatter', mode: 'lines+markers',
                  line: { color: '#c2410c', width: 2 },
                },
                {
                  x: intentTrends.map(t => t.week_start),
                  y: intentTrends.map(t => t.sentiment || 0),
                  name: 'Sentiment', type: 'scatter', mode: 'lines',
                  yaxis: 'y2', line: { color: '#1f7a3f', width: 2, dash: 'dot' },
                },
              ]}
              layout={{
                xaxis: { title: 'Week starting' },
                yaxis: { title: 'Share', tickformat: '.0%' },
                yaxis2: { title: 'Sentiment', overlaying: 'y', side: 'right' },
                plot_bgcolor: 'var(--bg-dark)', paper_bgcolor: 'var(--card-bg)',
                margin: { t: 20, r: 60, b: 50, l: 60 }, height: 300,
                hovermode: 'x unified', legend: { orientation: 'h', y: -0.25 },
              }}
              config={{ responsive: true, displaylogo: false, modeBarButtonsToRemove: ['pan2d', 'select2d', 'lasso2d'] }}
              style={{ width: '100%' }}
            />
            <WeekNote />
          </>
        ) : <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-lighter)' }}>No data available</div>}
      </Panel>
    </div>
  );
}

export default FieldSignal;
